import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiPlus, FiTrash2, FiUpload } from "react-icons/fi";
import companyService from "../../services/company.service";
import { BANK_NBFC_NAME_LABEL } from "../../constants/companyLabels";
import { REPO_ADMIN_POST_OPTIONS } from "../../constants/repoAdminPost";
import { loadRazorpayScript, openRazorpayCheckout } from "../../utils/razorpayCheckout";
import { getApiErrorMessage } from "../../utils/downloadExcel";
import useAuth from "../../hooks/useAuth";
import { landingPathForRole } from "../../utils/navReturn";
import PincodeAutoFill from "../../components/common/PincodeAutoFill";
import ManualDobFields from "../../components/common/ManualDobFields";
import { partsToDobIso, validateDobParts } from "../../utils/dateOfBirthUtils";
import "../../styles/users.css";
import "../../styles/profile.css";
import "../../styles/registerCompanyCompact.css";

const initialForm = {
  companyName: "",
  email: "",
  phone: "",
  address: "",
  panNumber: "",
  gstNumber: "",
  aadhaarNumber: "",
  adminName: "",
  adminEmail: "",
  adminPhone: "",
  adminPassword: "",
  adminDistrict: "",
  adminState: "",
  adminPincode: "",
  adminPost: "",
  adminAgencyName: "",
  adminDateOfBirth: "",
};

const ACTIVATION_SESSION_KEY = "fr_company_activation";
const TAX_DOC_MAX_BYTES = 5 * 1024 * 1024;
const TAX_DOC_TYPES = new Set(["image/jpeg", "image/png", "application/pdf"]);

function isTaxDocumentFile(file) {
  if (!file) return false;
  const mime = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  const extOk = /\.(jpe?g|png|pdf)$/.test(name);
  if (extOk && (!mime || TAX_DOC_TYPES.has(mime))) return true;
  return TAX_DOC_TYPES.has(mime);
}

function isTaxDocumentPdf(file) {
  if (!file) return false;
  const mime = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  return mime === "application/pdf" || name.endsWith(".pdf");
}

function formatInr(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN")}`;
}

function readActivationSession() {
  try {
    const raw = sessionStorage.getItem(ACTIVATION_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.companyId || !parsed?.activationToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeActivationSession(session) {
  sessionStorage.setItem(ACTIVATION_SESSION_KEY, JSON.stringify(session));
}

function clearActivationSession() {
  sessionStorage.removeItem(ACTIVATION_SESSION_KEY);
}

export default function RegisterCompany() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [registrationId, setRegistrationId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [activation, setActivation] = useState(null);
  const [paid, setPaid] = useState(false);
  const [directors, setDirectors] = useState([""]);
  const [companyPhotoFile, setCompanyPhotoFile] = useState(null);
  const [companyPhotoPreview, setCompanyPhotoPreview] = useState("");
  const [adminPhotoFile, setAdminPhotoFile] = useState(null);
  const [adminPhotoPreview, setAdminPhotoPreview] = useState("");
  const [panDocumentFile, setPanDocumentFile] = useState(null);
  const [panDocumentPreview, setPanDocumentPreview] = useState("");
  const [aadhaarDocumentFile, setAadhaarDocumentFile] = useState(null);
  const [aadhaarDocumentPreview, setAadhaarDocumentPreview] = useState("");
  const [gstDocumentFile, setGstDocumentFile] = useState(null);
  const [gstDocumentPreview, setGstDocumentPreview] = useState("");
  const [dobParts, setDobParts] = useState({ day: "", month: "", year: "" });
  const companyLogoInputRef = useRef(null);
  const adminPhotoInputRef = useRef(null);

  useEffect(() => {
    const session = readActivationSession();
    if (session) setActivation(session);
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendCooldown((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let next = value;
    if (name === "aadhaarNumber") {
      next = value.replace(/\D/g, "").slice(0, 12);
    } else if (name === "gstNumber") {
      next = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 15);
    }
    setForm((prev) => ({ ...prev, [name]: next }));
  };

  const handleAdminDobChange = (parts) => {
    setDobParts(parts);
    setForm((prev) => ({
      ...prev,
      adminDateOfBirth: partsToDobIso(parts),
    }));
  };

  const updateDirector = (index, value) => {
    setDirectors((prev) => prev.map((name, i) => (i === index ? value : name)));
  };

  const addDirector = () => {
    setDirectors((prev) => [...prev, ""]);
  };

  const removeDirector = (index) => {
    setDirectors((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const formatDirectorsForSave = () =>
    directors.map((name) => name.trim()).filter(Boolean).join(", ");

  const resolveAdminName = () => {
    const firstDirector = directors.map((name) => name.trim()).find(Boolean);
    return firstDirector || form.adminName.trim() || form.companyName.trim();
  };

  const handleImagePick = (kind, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file (JPEG, PNG, or WebP).");
      return;
    }
    const preview = URL.createObjectURL(file);
    if (kind === "company") {
      setCompanyPhotoFile(file);
      setCompanyPhotoPreview(preview);
    } else {
      setAdminPhotoFile(file);
      setAdminPhotoPreview(preview);
    }
    setError("");
  };

  const handleTaxDocumentPick = (kind, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isTaxDocumentFile(file)) {
      setError("Upload a JPG, JPEG, PNG, or PDF file for PAN, Aadhaar, and GST documents.");
      return;
    }
    if (file.size > TAX_DOC_MAX_BYTES) {
      setError("Each document must be 5 MB or smaller.");
      return;
    }
    const preview = isTaxDocumentPdf(file) ? "" : URL.createObjectURL(file);
    if (kind === "pan") {
      setPanDocumentFile(file);
      setPanDocumentPreview(preview);
    } else if (kind === "aadhaar") {
      setAadhaarDocumentFile(file);
      setAadhaarDocumentPreview(preview);
    } else {
      setGstDocumentFile(file);
      setGstDocumentPreview(preview);
    }
    setError("");
  };

  const resetOtpFlow = () => {
    setOtpSent(false);
    setRegistrationId("");
    setOtp("");
    setResendCooldown(0);
    setError("");
  };

  const validateForm = () => {
    if (!form.companyName.trim()) return `${BANK_NBFC_NAME_LABEL} is required`;
    if (!form.adminPost.trim()) return "Post is required";
    if (!formatDirectorsForSave()) return "Proprietor / partners / directors name is required";
    if (!form.email.trim()) return "Email is required";
    const dobError = validateDobParts(dobParts);
    if (dobError) return dobError;
    if (form.phone.replace(/\D/g, "").length < 10) {
      return "First confirmation number must be at least 10 digits.";
    }
    if (form.adminPhone.replace(/\D/g, "").length < 10) {
      return "Second confirmation number must be at least 10 digits.";
    }
    if (!form.panNumber.trim()) return "PAN number is required";
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(form.panNumber.trim())) {
      return "Enter a valid PAN number";
    }
    if (!form.aadhaarNumber.trim()) return "Aadhaar number is required";
    if (form.aadhaarNumber.replace(/\D/g, "").length !== 12) {
      return "Aadhaar number must be 12 digits";
    }
    if (!form.gstNumber.trim()) return "GST number is required";
    if (form.gstNumber.trim().length !== 15) return "GST number must be 15 digits";
    if (!panDocumentFile) return "PAN card is required";
    if (!aadhaarDocumentFile) return "Aadhaar card is required";
    if (!gstDocumentFile) return "GST certificate is required";
    if (!isTaxDocumentFile(panDocumentFile) || panDocumentFile.size > TAX_DOC_MAX_BYTES) {
      return "PAN card must be a JPG, JPEG, PNG, or PDF file of 5 MB or smaller.";
    }
    if (!isTaxDocumentFile(aadhaarDocumentFile) || aadhaarDocumentFile.size > TAX_DOC_MAX_BYTES) {
      return "Aadhaar card must be a JPG, JPEG, PNG, or PDF file of 5 MB or smaller.";
    }
    if (!isTaxDocumentFile(gstDocumentFile) || gstDocumentFile.size > TAX_DOC_MAX_BYTES) {
      return "GST certificate must be a JPG, JPEG, PNG, or PDF file of 5 MB or smaller.";
    }
    if (!form.address.trim()) return "Address is required";
    if (!form.adminDistrict.trim()) return "District is required";
    if (!form.adminState.trim()) return "State is required";
    if (!/^\d{6}$/.test(form.adminPincode.trim())) return "Valid 6-digit pincode is required";
    if (!form.adminPassword.trim() || form.adminPassword.length < 8) {
      return "Admin password must be at least 8 characters.";
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const directorsSaved = formatDirectorsForSave();
    const adminName = resolveAdminName();
    const email = form.email.trim();

    setLoading(true);
    try {
      const res = await companyService.registerCompany(
        {
          companyName: form.companyName.trim(),
          email,
          phone: form.phone.trim(),
          address: form.address.trim(),
          contactPersonName: directorsSaved,
          ownerName: directorsSaved,
          panNumber: form.panNumber.trim().toUpperCase(),
          aadhaarNumber: form.aadhaarNumber.replace(/\D/g, ""),
          gstNumber: form.gstNumber.trim().toUpperCase(),
          adminName,
          adminEmail: email,
          adminPhone: form.adminPhone.trim(),
          adminPassword: form.adminPassword,
          adminDistrict: form.adminDistrict.trim(),
          adminState: form.adminState.trim(),
          adminPincode: form.adminPincode.trim(),
          adminPost: form.adminPost.trim(),
          adminAgencyName: form.companyName.trim(),
          adminDateOfBirth: partsToDobIso(dobParts) || form.adminDateOfBirth.trim(),
        },
        {
          companyPhoto: companyPhotoFile,
          adminPhoto: adminPhotoFile,
          panDocument: panDocumentFile,
          aadhaarDocument: aadhaarDocumentFile,
          gstDocument: gstDocumentFile,
        }
      );
      setRegistrationId(res?.data?.registrationId || "");
      setOtpSent(Boolean(res?.requiresOtp));
      setResendCooldown(Number(res?.data?.resendAvailableInSeconds || 60));
    } catch (err) {
      setError(err?.response?.data?.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter the 6-digit OTP received by SMS.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await companyService.verifyCompanyRegistration(registrationId, otp);
      const company = res?.data?.company || {};
      const session = {
        companyId: company.id,
        companyName: company.companyName,
        companyCode: company.companyCode,
        status: company.status || "PENDING",
        activationToken: res?.data?.activationToken,
        activationFee: res?.data?.activationFee,
        contact: form.adminPhone.replace(/\D/g, "").slice(-10),
        email: form.email.trim(),
        name: resolveAdminName(),
      };
      writeActivationSession(session);
      setActivation(session);
      setOtpSent(false);
      setOtp("");
      setForm(initialForm);
      setDirectors([""]);
      setCompanyPhotoFile(null);
      setCompanyPhotoPreview("");
      setAdminPhotoFile(null);
      setAdminPhotoPreview("");
      setPanDocumentFile(null);
      setPanDocumentPreview("");
      setAadhaarDocumentFile(null);
      setAadhaarDocumentPreview("");
      setGstDocumentFile(null);
      setGstDocumentPreview("");
    } catch (err) {
      setError(err?.response?.data?.message || "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (loading || resendCooldown > 0) return;
    setLoading(true);
    setError("");
    try {
      const res = await companyService.resendCompanyRegistrationOtp(registrationId);
      setResendCooldown(Number(res?.data?.resendAvailableInSeconds || 60));
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to resend OTP.");
    } finally {
      setLoading(false);
    }
  };

  const enterDashboard = async (payload) => {
    const session = payload?.data || payload || {};
    if (!session?.token || !session?.user) return false;
    const permissions = await login({
      user: session.user,
      token: session.token,
    });
    clearActivationSession();
    navigate(landingPathForRole(session.user.role, permissions), { replace: true });
    return true;
  };

  const handlePay = async () => {
    if (!activation?.companyId || !activation?.activationToken) {
      setError("Activation session missing. Please register again.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const orderRes = await companyService.createRegistrationActivationOrder({
        companyId: activation.companyId,
        activationToken: activation.activationToken,
      });
      const data = orderRes?.data || {};
      if (data.alreadyActivated) {
        if (await enterDashboard(orderRes)) return;
        setPaid(true);
        clearActivationSession();
        return;
      }
      await loadRazorpayScript();
      const checkout = await openRazorpayCheckout({
        key: data.razorpayKeyId,
        orderId: data.orderId,
        amount: data.amount,
        currency: data.currency,
        description: "₹100 first-user activation fee",
        prefill: {
          name: form.adminName || activation.name || activation.companyName || "",
          email: form.adminEmail || activation.email || "",
          contact: activation.contact || "",
        },
      });
      const verifyRes = await companyService.verifyRegistrationActivationPayment({
        companyId: activation.companyId,
        activationToken: activation.activationToken,
        razorpay_order_id: checkout.razorpay_order_id,
        razorpay_payment_id: checkout.razorpay_payment_id,
        razorpay_signature: checkout.razorpay_signature,
        paymentId: data.paymentId,
      });
      if (await enterDashboard(verifyRes)) return;
      setPaid(true);
      clearActivationSession();
    } catch (err) {
      if (err?.message === "CHECKOUT_CLOSED") {
        setError("Payment was cancelled. Your company remains pending activation.");
      } else if (err?.message === "PAYMENT_FAILED") {
        setError("Payment failed. Your company remains pending activation.");
      } else {
        setError(
          await getApiErrorMessage(
            err,
            "Payment failed. Your company remains pending activation."
          )
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const feeLabel = formatInr(activation?.activationFee);

  if (paid) {
    return (
      <div className="page create-company-page repo-form-page register-company-page">
        <div className="create-company-header">
          <div>
            <h2>✓ Payment Successful</h2>
            <p className="muted">Company live — 1 user activation included</p>
          </div>
        </div>
        <div className="create-company-card">
          <p className="cfm-status">
            Registration was free. Your ₹100 payment activated the company and includes
            one user. Additional users are ₹100 each when you activate them.
          </p>
          <div className="create-company-actions">
            <Link to="/home" className="primary-page-btn">
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (activation?.companyId) {
    return (
      <div className="page create-company-page repo-form-page register-company-page">
        <div className="create-company-header">
          <div>
            <h2>Registration Submitted</h2>
            <p className="muted">
              Registration is free. Pay ₹100 to activate your first user and go live.
            </p>
          </div>
          <Link to="/" className="secondary-page-btn">
            Back to login
          </Link>
        </div>
        <div className="create-company-card">
          <div className="form-grid two-column">
            <div className="form-group">
              <label>Company</label>
              <input value={activation.companyName || ""} readOnly />
            </div>
            <div className="form-group">
              <label>Status</label>
              <input value="PENDING ACTIVATION" readOnly />
            </div>
            <div className="form-group">
              <label>Registration Fee</label>
              <input value="₹0 (Free)" readOnly />
            </div>
            <div className="form-group">
              <label>First User Activation Fee</label>
              <input value={feeLabel} readOnly />
            </div>
            <div className="form-group">
              <label>Refund</label>
              <input value="Non-refundable" readOnly />
            </div>
          </div>
          {error && <p className="error-text">{error}</p>}
          <div className="create-company-actions">
            <button
              type="button"
              className="primary-page-btn"
              onClick={handlePay}
              disabled={loading}
            >
              {loading ? "Opening checkout…" : `Pay ${feeLabel} & Activate`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page create-company-page repo-form-page register-company-page">
      <div className="create-company-header">
        <div>
          <h2>Register your company</h2>
          <p className="muted">
            Registration is free. After OTP you pay ₹100 to activate your first user.
            Each extra user is ₹100 when you activate them. Monthly plans are separate.
          </p>
        </div>
        <Link to="/" className="secondary-page-btn">
          Back to login
        </Link>
      </div>


      {!otpSent ? (
        <form className="create-company-form" onSubmit={handleSubmit}>
          <div className="create-company-card">
            <section
              className="create-company-section create-company-section--ssdi-photos"
              aria-label="Photos"
            >
              <div className="create-company-uploads-row">
                <div className="create-company-upload-slot">
                  <span className="create-company-upload-slot__label">Company logo</span>
                  <button
                    type="button"
                    className="create-company-upload-slot__picker"
                    onClick={() => companyLogoInputRef.current?.click()}
                  >
                    <span className="create-company-upload-preview" aria-hidden>
                      {companyPhotoPreview ? (
                        <img src={companyPhotoPreview} alt="" />
                      ) : (
                        <span className="create-company-upload-preview__placeholder">
                          {(form.companyName || "?").charAt(0).toUpperCase()}
                        </span>
                      )}
                    </span>
                    <span className="create-company-upload-slot__action">
                      <FiUpload aria-hidden />
                      {companyPhotoPreview ? "Change logo" : "Upload logo"}
                    </span>
                  </button>
                  <input
                    ref={companyLogoInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleImagePick("company", e)}
                  />
                </div>

                <div className="create-company-upload-slot">
                  <span className="create-company-upload-slot__label">Admin photo</span>
                  <button
                    type="button"
                    className="create-company-upload-slot__picker"
                    onClick={() => adminPhotoInputRef.current?.click()}
                  >
                    <span className="create-company-upload-preview" aria-hidden>
                      {adminPhotoPreview ? (
                        <img src={adminPhotoPreview} alt="" />
                      ) : (
                        <span className="create-company-upload-preview__placeholder">
                          {(resolveAdminName() || "?").charAt(0).toUpperCase()}
                        </span>
                      )}
                    </span>
                    <span className="create-company-upload-slot__action">
                      <FiUpload aria-hidden />
                      {adminPhotoPreview ? "Change photo" : "Upload photo"}
                    </span>
                  </button>
                  <input
                    ref={adminPhotoInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleImagePick("admin", e)}
                  />
                </div>
              </div>
            </section>

            <section className="create-company-section" aria-labelledby="register-section-firm">
              <header className="create-company-section__head" id="register-section-firm">
                <h3>Firm/agency details</h3>
              </header>
              <div className="form-grid two-column">
                <div className="form-group full-width">
                  <label>{BANK_NBFC_NAME_LABEL} *</label>
                  <input
                    name="companyName"
                    placeholder={`Enter ${BANK_NBFC_NAME_LABEL.toLowerCase()}`}
                    value={form.companyName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group full-width">
                  <label htmlFor="registerAdminPost">Post *</label>
                  <select
                    id="registerAdminPost"
                    name="adminPost"
                    value={form.adminPost}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select post</option>
                    {REPO_ADMIN_POST_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group full-width directors-field">
                  <label>Proprietor / Partners / Directors name *</label>
                  {directors.map((name, index) => (
                    <div key={`director-${index}`} className="director-row">
                      <input
                        type="text"
                        value={name}
                        placeholder={
                          directors.length > 1
                            ? `Director ${index + 1} name`
                            : "Enter proprietor / partners / directors name"
                        }
                        onChange={(e) => updateDirector(index, e.target.value)}
                        required={index === 0}
                      />
                      {directors.length > 1 && (
                        <button
                          type="button"
                          className="secondary-page-btn director-row__remove"
                          onClick={() => removeDirector(index)}
                          aria-label={`Remove director ${index + 1}`}
                        >
                          <FiTrash2 aria-hidden />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="primary-page-btn director-row__add"
                    onClick={addDirector}
                  >
                    <FiPlus aria-hidden />
                    Add another director/partner
                  </button>
                </div>
              </div>
            </section>

            <section className="create-company-section" aria-labelledby="register-section-contact">
              <header className="create-company-section__head" id="register-section-contact">
                <h3>Contact</h3>
              </header>
              <div className="form-grid two-column profile-contact-grid">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter email"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group form-group--dob">
                  <label htmlFor="register-admin-dob-day">Date of birth *</label>
                  <ManualDobFields
                    value={form.adminDateOfBirth}
                    onChange={handleAdminDobChange}
                    idPrefix="register-admin-dob"
                  />
                  <p className="field-hint muted">DD / MM / YYYY</p>
                </div>
                <div className="form-group">
                  <label>First Confirmation Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Enter first confirmation number"
                    value={form.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Second Confirmation Number *</label>
                  <input
                    type="tel"
                    name="adminPhone"
                    placeholder="Enter second confirmation number"
                    value={form.adminPhone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </section>

            <section className="create-company-section" aria-labelledby="register-section-tax">
              <header className="create-company-section__head" id="register-section-tax">
                <h3>Tax &amp; registration</h3>
              </header>
              <div className="form-grid create-company-tax-grid">
                <div className="form-group">
                  <label>PAN Number *</label>
                  <input
                    name="panNumber"
                    placeholder="Enter PAN number"
                    value={form.panNumber}
                    onChange={handleChange}
                    maxLength={10}
                    required
                    style={{ textTransform: "uppercase" }}
                  />
                  <label htmlFor="register-pan-document">Upload PAN Card *</label>
                  <input
                    id="register-pan-document"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                    onChange={(e) => handleTaxDocumentPick("pan", e)}
                    required={!panDocumentFile}
                  />
                  {panDocumentFile ? (
                    <div className="tax-doc-upload">
                      {panDocumentPreview ? (
                        <img
                          className="tax-doc-upload__preview"
                          src={panDocumentPreview}
                          alt=""
                        />
                      ) : null}
                      <span className="tax-doc-upload__name">{panDocumentFile.name}</span>
                    </div>
                  ) : null}
                </div>
                <div className="form-group">
                  <label>Aadhaar Number *</label>
                  <input
                    name="aadhaarNumber"
                    placeholder="12-digit Aadhaar number"
                    value={form.aadhaarNumber}
                    onChange={handleChange}
                    inputMode="numeric"
                    maxLength={12}
                    required
                  />
                  <label htmlFor="register-aadhaar-document">Upload Aadhaar Card *</label>
                  <input
                    id="register-aadhaar-document"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                    onChange={(e) => handleTaxDocumentPick("aadhaar", e)}
                    required={!aadhaarDocumentFile}
                  />
                  {aadhaarDocumentFile ? (
                    <div className="tax-doc-upload">
                      {aadhaarDocumentPreview ? (
                        <img
                          className="tax-doc-upload__preview"
                          src={aadhaarDocumentPreview}
                          alt=""
                        />
                      ) : null}
                      <span className="tax-doc-upload__name">{aadhaarDocumentFile.name}</span>
                    </div>
                  ) : null}
                </div>
                <div className="form-group">
                  <label>GST Number *</label>
                  <input
                    name="gstNumber"
                    placeholder="15-digit GST number"
                    value={form.gstNumber}
                    onChange={handleChange}
                    maxLength={15}
                    required
                    style={{ textTransform: "uppercase" }}
                  />
                  <label htmlFor="register-gst-document">Upload GST Certificate *</label>
                  <input
                    id="register-gst-document"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                    onChange={(e) => handleTaxDocumentPick("gst", e)}
                    required={!gstDocumentFile}
                  />
                  {gstDocumentFile ? (
                    <div className="tax-doc-upload">
                      {gstDocumentPreview ? (
                        <img
                          className="tax-doc-upload__preview"
                          src={gstDocumentPreview}
                          alt=""
                        />
                      ) : null}
                      <span className="tax-doc-upload__name">{gstDocumentFile.name}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </section>

            <section className="create-company-section" aria-labelledby="register-section-location">
              <header className="create-company-section__head" id="register-section-location">
                <h3>Address</h3>
              </header>
              <div className="form-grid two-column">
                <div className="form-group full-width">
                  <label>Address *</label>
                  <input
                    name="address"
                    placeholder="Enter firm / agency address"
                    value={form.address}
                    onChange={handleChange}
                    required
                  />
                </div>
                <PincodeAutoFill
                  pincode={form.adminPincode}
                  onPincodeChange={(value) =>
                    setForm((prev) => ({ ...prev, adminPincode: value }))
                  }
                  city={form.adminDistrict}
                  onCityChange={(value) =>
                    setForm((prev) => ({ ...prev, adminDistrict: value }))
                  }
                  state={form.adminState}
                  onStateChange={(value) =>
                    setForm((prev) => ({ ...prev, adminState: value }))
                  }
                  cityLabel="District"
                  pincodeId="register-pincode"
                  cityId="register-district"
                  stateId="registerAdminState"
                  pincodeName="adminPincode"
                  cityName="adminDistrict"
                />
                <div className="form-group full-width">
                  <label>Password *</label>
                  <input
                    type="password"
                    name="adminPassword"
                    placeholder="Enter secure password (min. 8 characters)"
                    value={form.adminPassword}
                    onChange={handleChange}
                    minLength={8}
                    autoComplete="new-password"
                    required
                  />
                  <p className="field-hint muted">
                    Enter a secure password with at least 8 characters.
                  </p>
                </div>
              </div>
            </section>
          </div>

          {error && <p className="error-text">{error}</p>}

          <div className="create-company-actions">
            <button type="button" className="secondary-page-btn" onClick={() => navigate("/repo-admin/login")}>
              Go to repo admin login
            </button>
            <button type="submit" className="primary-page-btn" disabled={loading}>
              {loading ? "Submitting…" : "Submit registration"}
            </button>
          </div>
        </form>
      ) : (
        <form className="create-company-form" onSubmit={handleVerifyOtp}>
          <div className="create-company-card">
            <h3>Verify Mobile Number</h3>
            <p className="muted">
              We sent an OTP to {form.phone.replace(/\d(?=\d{4})/g, "*")}.
            </p>
            <div className="form-group">
              <label htmlFor="company-registration-otp">Enter OTP</label>
              <input
                id="company-registration-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit OTP"
                required
              />
            </div>
            {resendCooldown > 0 ? <p className="muted">Resend available in {resendCooldown}s</p> : null}
            {error && <p className="error-text">{error}</p>}
            <div className="create-company-actions">
              <button type="button" className="secondary-page-btn" onClick={resetOtpFlow} disabled={loading}>
                Back / Edit
              </button>
              <button type="button" className="secondary-page-btn" onClick={handleResendOtp} disabled={loading || resendCooldown > 0}>
                Resend OTP
              </button>
              <button type="submit" className="primary-page-btn" disabled={loading || otp.length !== 6}>
                {loading ? "Verifying…" : "Verify OTP"}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
